import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuFileText, LuImage, LuPaperclip, LuX } from 'react-icons/lu';

import { FieldLabel } from '../../../../styles/dunning/base';
import {
   AttachButton,
   AttachError,
   DropArea,
   DropHint,
   HiddenFileInput,
   RemoveButton,
   SelectedList,
   SelectedName,
   SelectedRow,
   SelectedSize,
} from '../../../../styles/dunning/attachments';
import {
   ATTACHMENT_ACCEPT,
   ATTACHMENT_MAX_BYTES,
   ATTACHMENT_MAX_FILES,
   ATTACHMENT_MIMES,
   ATTACHMENT_MIMES_LABEL,
} from '../constants';
import { formatFileSize } from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Seleção de anexos da tratativa (handoff 0096) — arquivos LOCAIS, ainda não
// enviados: sobem junto com o POST da tratativa, num multipart atômico.
//
// Valida mime/tamanho/quantidade AQUI para o operador saber na hora; o backend
// revalida (422 invalid_file_type / upload_limit). Os limites vêm de
// `constants.js`, que espelha o contrato — nunca afrouxar por conta própria.
//
// Erro de validação é INLINE (FC4 B.3: erro de campo não vira toast).
//
// A microcópia diz que não dá para anexar depois porque não dá mesmo: o upload
// só existe na criação da tratativa (0096 §5.1) — descobrir isso no fim seria
// cruel com quem acabou de escrever o relato.
// ---------------------------------------------------------------------------

function fileIconFor(mimeType) {
   return String(mimeType ?? '').startsWith('image/') ? LuImage : LuFileText;
}

// Identidade local do arquivo escolhido (não existe id antes do upload) — serve
// de key da lista e de critério de duplicata.
function fileKey(file) {
   return `${file.name}-${file.size}-${file.lastModified}`;
}

// Devolve { accepted, errors } — nunca lança. O que não passa é descartado com
// a razão nomeando o arquivo (o operador precisa saber QUAL falhou).
function partitionFiles(incoming, current) {
   const accepted = [];
   const errors = [];
   const seen = new Set(current.map(fileKey));
   let remaining = ATTACHMENT_MAX_FILES - current.length;

   incoming.forEach((file) => {
      if (seen.has(fileKey(file))) {
         errors.push(`${file.name}: já está na lista.`);
         return;
      }
      if (!ATTACHMENT_MIMES.includes(file.type)) {
         errors.push(`${file.name}: tipo não aceito (envie ${ATTACHMENT_MIMES_LABEL}).`);
         return;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
         errors.push(
            `${file.name}: ${formatFileSize(file.size)} excede o limite de 10 MB.`
         );
         return;
      }
      if (remaining <= 0) {
         errors.push(
            `${file.name}: limite de ${ATTACHMENT_MAX_FILES} anexos por tratativa.`
         );
         return;
      }
      remaining -= 1;
      seen.add(fileKey(file));
      accepted.push(file);
   });

   return { accepted, errors };
}

export default function AttachmentPicker({ files, onChange, disabled = false }) {
   const inputRef = useRef(null);
   const [isDragging, setIsDragging] = useState(false);
   const [errors, setErrors] = useState([]);

   const isFull = files.length >= ATTACHMENT_MAX_FILES;
   const isBlocked = disabled || isFull;

   const addFiles = (incoming) => {
      const list = Array.from(incoming ?? []);
      if (!list.length) return;
      const { accepted, errors: rejected } = partitionFiles(list, files);
      setErrors(rejected);
      if (accepted.length) onChange([...files, ...accepted]);
   };

   const handleRemove = (index) => {
      setErrors([]);
      onChange(files.filter((_, position) => position !== index));
   };

   const handleDrop = (event) => {
      event.preventDefault();
      setIsDragging(false);
      if (isBlocked) return;
      addFiles(event.dataTransfer?.files);
   };

   return (
      <div>
         <FieldLabel as="span">
            Anexos {files.length > 0 && `(${files.length}/${ATTACHMENT_MAX_FILES})`}
         </FieldLabel>

         <DropArea
            $dragging={isDragging}
            onDragOver={(event) => {
               event.preventDefault();
               if (!isBlocked) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
         >
            <AttachButton
               type="button"
               disabled={isBlocked}
               onClick={() => inputRef.current?.click()}
            >
               <LuPaperclip size={14} />
               Anexar arquivo
            </AttachButton>
            <DropHint>
               {isFull
                  ? `Limite de ${ATTACHMENT_MAX_FILES} anexos atingido.`
                  : `Inclua na tratativa até ${ATTACHMENT_MAX_FILES} arquivos (${ATTACHMENT_MIMES_LABEL}, 10 MB cada).`}
            </DropHint>

            <HiddenFileInput
               ref={inputRef}
               type="file"
               multiple
               accept={ATTACHMENT_ACCEPT}
               onChange={(event) => {
                  addFiles(event.target.files);
                  // Zera para permitir reescolher o MESMO arquivo depois de
                  // removê-lo (sem isso o input não dispara change de novo).
                  if (inputRef.current) inputRef.current.value = '';
               }}
            />
         </DropArea>

         {files.length > 0 && (
            <SelectedList>
               {files.map((file, index) => {
                  const Icon = fileIconFor(file.type);
                  return (
                     <SelectedRow key={fileKey(file)}>
                        <Icon size={14} />
                        <SelectedName title={file.name}>{file.name}</SelectedName>
                        <SelectedSize>{formatFileSize(file.size)}</SelectedSize>
                        <RemoveButton
                           type="button"
                           title="Remover anexo"
                           aria-label={`Remover ${file.name}`}
                           disabled={disabled}
                           onClick={() => handleRemove(index)}
                        >
                           <LuX size={13} />
                        </RemoveButton>
                     </SelectedRow>
                  );
               })}
            </SelectedList>
         )}

         {errors.length > 0 && (
            <AttachError>
               {errors.map((message) => (
                  <div key={message}>{message}</div>
               ))}
            </AttachError>
         )}
      </div>
   );
}

AttachmentPicker.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   files: PropTypes.array.isRequired,
   onChange: PropTypes.func.isRequired,
   // eslint-disable-next-line react/require-default-props
   disabled: PropTypes.bool,
};
